import React, { useState, useEffect, useMemo } from 'react';
import { useJobs } from '../services/JobContext';
import { useLocation } from 'react-router-dom';
import { 
  initializeGoogleCalendar, 
  signIn, 
  signOut, 
  createCalendarEvent, 
  updateCalendarEvent, 
  deleteCalendarEvent, 
  isSignedIn,
  getSignedInCount,
  getTotalClients 
} from '../services/googleCalendar';
import { INTERVIEW_TYPES } from '../constants';
import { getAvailableRounds, formatJobForDisplay } from '../utils/helpers';
import ConfirmDialog from './ConfirmDialog';

// Helper to format Date to datetime-local input format (YYYY-MM-DDTHH:MM)
const formatDateTimeLocal = (dateString) => {
  if (!dateString) return '';
  const dt = new Date(dateString);
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

// Strict-enough email validator (local-part@domain.tld, no spaces)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (email) => EMAIL_RE.test(String(email || '').trim());

// Parse a free-form attendee string using commas, semicolons, whitespace, or newlines
const parseAttendeeList = (raw) =>
  String(raw || '')
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);

const InterviewScheduler = () => {
  const { jobs, addInterview, getJobInterviews, updateInterview, deleteInterview } = useJobs();
  const location = useLocation();
  const preSelectedJobId = useMemo(() => new URLSearchParams(location.search).get('jobId'), [location.search]);
  const editInterviewId = useMemo(() => new URLSearchParams(location.search).get('editInterviewId'), [location.search]);
  const [isGoogleSignedIn, setIsGoogleSignedIn] = useState(false);
  const [calendarClientCount, setCalendarClientCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedJobHistory, setSelectedJobHistory] = useState([]);
  const [editingInterview, setEditingInterview] = useState(null);
  const [showCalendarWarning, setShowCalendarWarning] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [defaultAttendees, setDefaultAttendees] = useState(() => {
    try {
      const saved = localStorage.getItem('defaultAttendees');
      const parsed = saved ? JSON.parse(saved) : [];
      // Drop any legacy entries that don't pass the stricter validator
      const cleaned = Array.isArray(parsed)
        ? parsed.filter((e) => isValidEmail(e))
        : [];
      if (cleaned.length !== (parsed?.length || 0)) {
        localStorage.setItem('defaultAttendees', JSON.stringify(cleaned));
      }
      return cleaned;
    } catch {
      return [];
    }
  });
  const [newDefaultEmail, setNewDefaultEmail] = useState('');
  const [formData, setFormData] = useState({
    jobId: '',
    round: '',
    interviewType: '',
    dateTime: '',
    duration: 30,
    attendees: '',
    notes: '',
    recruiterName: '',
    meetingLink: '',
    status: 'waiting',
    isFinalRound: false
  });

  // Load default attendees into form when not editing
  useEffect(() => {
    if (!editingInterview && defaultAttendees.length > 0) {
      setFormData(prev => ({
        ...prev,
        attendees: prev.attendees || defaultAttendees.join(', ')
      }));
    }
  }, [defaultAttendees, editingInterview]);

  const addDefaultAttendee = () => {
    const email = newDefaultEmail.trim().toLowerCase();
    if (!email) return;
    if (!isValidEmail(email)) {
      alert(`"${email}" is not a valid email address.`);
      return;
    }
    if (defaultAttendees.includes(email)) return;
    const updated = [...defaultAttendees, email];
    setDefaultAttendees(updated);
    localStorage.setItem('defaultAttendees', JSON.stringify(updated));
    setNewDefaultEmail('');
    // Also add to current form if not already there
    const currentAttendees = formData.attendees.split(',').map(e => e.trim()).filter(Boolean);
    if (!currentAttendees.includes(email)) {
      setFormData(prev => ({
        ...prev,
        attendees: prev.attendees ? `${prev.attendees}, ${email}` : email
      }));
    }
  };

  const removeDefaultAttendee = (email) => {
    const updated = defaultAttendees.filter(e => e !== email);
    setDefaultAttendees(updated);
    localStorage.setItem('defaultAttendees', JSON.stringify(updated));
  };

  const toggleAttendeeInForm = (email) => {
    const currentAttendees = formData.attendees.split(',').map(e => e.trim()).filter(Boolean);
    let updated;
    if (currentAttendees.includes(email)) {
      updated = currentAttendees.filter(e => e !== email);
    } else {
      updated = [...currentAttendees, email];
    }
    setFormData(prev => ({ ...prev, attendees: updated.join(', ') }));
  };



  useEffect(() => {
    const initGoogle = async () => {
      try {
        // Wait for Google scripts to load
        const waitForGoogleScripts = () => {
          return new Promise((resolve) => {
            const checkScripts = () => {
              if (window.gapi && window.google?.accounts?.oauth2) {
                resolve(true);
              } else {
                setTimeout(checkScripts, 100);
              }
            };
            checkScripts();
          });
        };

        await waitForGoogleScripts();
        
        const initialized = await initializeGoogleCalendar();
        if (initialized) {
          const count = getSignedInCount();
          setIsGoogleSignedIn(count > 0);
          setCalendarClientCount(count);
        }
      } catch (error) {
        console.error('Failed to initialize Google Calendar:', error);
      }
    };

    initGoogle();

    // Check for pre-selected job from URL parameters
    if (preSelectedJobId && jobs.find(job => job.id === preSelectedJobId)) {
      const jobInterviews = getJobInterviews(preSelectedJobId).sort((a, b) => a.round - b.round);
      setSelectedJobHistory(jobInterviews);
      
      // Check if we need to edit a specific interview
      if (editInterviewId) {
        const interviewToEdit = jobInterviews.find(i => i.id === editInterviewId);
        if (interviewToEdit) {
          setEditingInterview(interviewToEdit);
          setFormData({
            jobId: interviewToEdit.jobId,
            round: interviewToEdit.round,
            interviewType: interviewToEdit.interviewType,
            dateTime: formatDateTimeLocal(interviewToEdit.dateTime),
            duration: interviewToEdit.duration,
            attendees: interviewToEdit.attendees ? interviewToEdit.attendees.join(', ') : '',
            notes: interviewToEdit.notes || '',
            recruiterName: interviewToEdit.recruiterName || '',
            meetingLink: interviewToEdit.meetingLink || '',
            status: interviewToEdit.status || 'waiting',
            isFinalRound: interviewToEdit.isFinalRound || false
          });
          return;
        }
      }
      
      // Set form data for new interview
      setFormData(prev => ({ ...prev, jobId: preSelectedJobId }));
      
      // Set the next available round
      const availableRounds = getAvailableRoundsForJob(jobInterviews);
      if (availableRounds.length > 0) {
        setFormData(prev => ({ ...prev, round: availableRounds[0] }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedJobId, editInterviewId, jobs]);

  const handleGoogleSignIn = async () => {
    try {
      await signIn((count, total) => {
        setIsGoogleSignedIn(count > 0);
        setCalendarClientCount(count);
      });
    } catch (error) {
      console.error('Sign in failed:', error);
      const errorMsg = error?.error === 'popup_closed_by_user'
        ? 'Sign-in cancelled. Please try again and complete the Google sign-in process.'
        : 'Failed to sign in to Google Calendar. Please try again or check if popups are blocked.';
      alert(errorMsg);
    }
  };

  const handleGoogleSignOut = () => {
    signOut();
    setIsGoogleSignedIn(false);
    setCalendarClientCount(0);
    alert('Signed out from Google Calendar');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    
    setFormData({
      ...formData,
      [name]: finalValue
    });
    
    // Load job history when job is selected
    if (name === 'jobId' && value) {
      const jobInterviews = getJobInterviews(value).sort((a, b) => a.round - b.round);
      setSelectedJobHistory(jobInterviews);
      
      // Reset round to first available when job changes
      const availableRounds = getAvailableRoundsForJob(jobInterviews);
      if (availableRounds.length > 0) {
        setFormData(prev => ({ ...prev, round: availableRounds[0] }));
      }
    }
  };

  const getAvailableRoundsForJob = (jobInterviews, forDisplay = false) => {
    return getAvailableRounds(jobInterviews, forDisplay ? null : editingInterview);
  };

  const getCurrentAvailableRounds = () => {
    if (!formData.jobId) return [1];
    return getAvailableRoundsForJob(selectedJobHistory);
  };

  const handleDeleteInterview = (interviewId) => {
    setPendingDeleteId(interviewId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteInterview = async () => {
    if (pendingDeleteId) {
      try {
        // Find the interview to get its Google Event ID
        const interviewToDelete = selectedJobHistory.find(i => i.id === pendingDeleteId);
        
        // Delete from Google Calendar if signed in and event exists
        if (isGoogleSignedIn && interviewToDelete?.googleEventId) {
          try {
            await deleteCalendarEvent(interviewToDelete.googleEventId);
          } catch (error) {
            console.warn('Failed to delete Google Calendar event:', error);
            // Continue with local deletion even if calendar deletion fails
          }
        }
        
        // Delete from local database
        deleteInterview(pendingDeleteId);
        const updatedHistory = getJobInterviews(formData.jobId).sort((a, b) => a.round - b.round);
        setSelectedJobHistory(updatedHistory);
      } catch (error) {
        console.error('Error deleting interview:', error);
        alert('Failed to delete interview. Please try again.');
        return;
      }
    }
    setShowDeleteConfirm(false);
    setPendingDeleteId(null);
  };

  const cancelDeleteInterview = () => {
    setShowDeleteConfirm(false);
    setPendingDeleteId(null);
  };

  const handleEditInterview = (interview) => {
    setEditingInterview(interview);
    setFormData({
      jobId: interview.jobId,
      round: interview.round,
      interviewType: interview.interviewType,
      dateTime: formatDateTimeLocal(interview.dateTime),
      duration: interview.duration,
      attendees: interview.attendees ? interview.attendees.join(', ') : '',
      notes: interview.notes || '',
      recruiterName: interview.recruiterName || '',
      meetingLink: interview.meetingLink || '',
      status: interview.status || 'waiting'
    });
  };

  const handleCancelEdit = () => {
    setEditingInterview(null);
    setFormData({
      jobId: '',
      round: 1,
      interviewType: 'Technical',
      dateTime: '',
      duration: 60,
      attendees: '',
      notes: '',
      recruiterName: '',
      meetingLink: '',
      status: 'waiting'
    });
  };

  const handleConfirmWithoutCalendar = () => {
    setShowCalendarWarning(false);
    if (pendingSubmit) {
      handleSubmit(null, true);
    }
    setPendingSubmit(null);
  };

  const handleCancelWarning = () => {
    setShowCalendarWarning(false);
    setPendingSubmit(null);
  };

  const handleSubmit = async (e, skipCalendarWarning = false) => {
    e?.preventDefault();

    if (isSubmitting) return;
    
    if (!formData.jobId || !formData.dateTime) {
      alert('Please select a job and date/time');
      return;
    }

    const selectedJob = jobs.find(job => job.id === formData.jobId);
    if (!selectedJob) {
      alert('Selected job not found');
      return;
    }

    // Check if round already exists (only for new interviews)
    if (!editingInterview) {
      const existingRound = selectedJobHistory.find(interview => interview.round === parseInt(formData.round));
      if (existingRound) {
        alert(`Round ${formData.round} already exists for this job. Please select a different round.`);
        return;
      }
      
      // Check if previous round exists (sequential validation)
      const selectedRound = parseInt(formData.round);
      if (selectedRound > 1) {
        const previousRound = selectedJobHistory.find(interview => interview.round === selectedRound - 1);
        if (!previousRound) {
          alert(`Round ${selectedRound - 1} must be completed before scheduling Round ${selectedRound}.`);
          return;
        }
      }
    }

    // Show warning if not connected to Google Calendar
    if (!isGoogleSignedIn && !skipCalendarWarning) {
      setPendingSubmit({ selectedJob });
      setShowCalendarWarning(true);
      return;
    }

    setIsSubmitting(true);
    try {
      // Auto-update previous round status if scheduling next round
      const selectedRound = parseInt(formData.round);
      if (!editingInterview && selectedRound > 1) {
        const previousRound = selectedJobHistory.find(interview => interview.round === selectedRound - 1);
        if (previousRound && previousRound.status === 'waiting') {
          updateInterview(previousRound.id, { status: 'converted' });
        }
      }

      // Parse + validate attendee emails before hitting Google Calendar
      const parsedAttendees = parseAttendeeList(formData.attendees);
      const invalidAttendees = parsedAttendees.filter((email) => !isValidEmail(email));
      if (invalidAttendees.length > 0) {
        setIsSubmitting(false);
        alert(
          `The following attendee email${invalidAttendees.length === 1 ? ' is' : 's are'} invalid and must be fixed before scheduling:\n\n` +
            invalidAttendees.map((e) => `• ${e}`).join('\n')
        );
        return;
      }
      // Deduplicate
      const attendees = Array.from(new Set(parsedAttendees));

      // Create or update interview record
      const interviewData = {
        ...formData,
        attendees,
      };
      
      // Create or update Google Calendar event if signed in.
      // Calendar failures (quota/rate-limit/network) must NOT block saving the
      // interview record — they are surfaced as a separate, clear message below.
      let calendarEventId = editingInterview?.googleEventId;
      let calendarError = null;

      if (isGoogleSignedIn) {
        // Format datetime for PKT timezone (don't use toISOString as it converts to UTC)
        const formatForCalendar = (date) => {
          const pad = (n) => String(n).padStart(2, '0');
          return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
        };
        
        const startDateTime = new Date(formData.dateTime);
        const endDateTime = new Date(startDateTime.getTime() + (formData.duration * 60000));
        const startFormatted = formatForCalendar(startDateTime);
        const endFormatted = formatForCalendar(endDateTime);

        // Build description with clean formatting
        const descriptionParts = [
          '📋 INTERVIEW DETAILS',
          `🎯 Interview Type: ${formData.interviewType}`,
          `👤 BD: ${selectedJob.bdName}`,
        ];
        
        if (formData.recruiterName) {
          descriptionParts.push(`📞 Recruiter: ${formData.recruiterName}`);
        }
        
        if (formData.meetingLink) {
          descriptionParts.push('');
          descriptionParts.push('🔗 MEETING LINK');
          descriptionParts.push(formData.meetingLink);
        }
        
        if (selectedJob.resumeLink || selectedJob.jdLink) {
          descriptionParts.push('');
          descriptionParts.push('📎 DOCUMENTS');
          if (selectedJob.resumeLink) {
            descriptionParts.push(`📄 Resume: ${selectedJob.resumeLink}`);
          }
          if (selectedJob.jdLink) {
            descriptionParts.push(`📝 JD: ${selectedJob.jdLink}`);
          }
        }
        
        if (formData.notes) {
          descriptionParts.push('');
          descriptionParts.push('📌 NOTES');
          descriptionParts.push(formData.notes);
        }
        
        const description = descriptionParts.join('\n');

        const eventDetails = {
          title: `${selectedJob.companyName} - ${selectedJob.position} (Round ${formData.round})`,
          description,
          startDateTime: startFormatted,
          endDateTime: endFormatted,
          attendees: interviewData.attendees
        };

        // Update existing calendar event or create new one.
        // Any failure here is captured but does NOT block the DB save below.
        try {
          if (editingInterview && calendarEventId) {
            await updateCalendarEvent(calendarEventId, eventDetails);
          } else {
            const calendarEvent = await createCalendarEvent(eventDetails);
            calendarEventId = calendarEvent.id;
          }
        } catch (err) {
          calendarError = err;
          console.warn('Google Calendar call failed; saving interview record anyway.', err);
          // If auth expired, reflect that in UI so user can re-sign-in for next time
          if (err?.message?.includes('Google Calendar authentication expired')) {
            setIsGoogleSignedIn(false);
            setCalendarClientCount(0);
          }
        }
      }

      // Add calendar event ID to interview data (only when we actually have one)
      if (calendarEventId) {
        interviewData.googleEventId = calendarEventId;
      }

      if (editingInterview) {
        updateInterview(editingInterview.id, interviewData);
      } else {
        addInterview(interviewData);
      }

      // Build an accurate success message based on whether calendar succeeded
      const baseMsg = editingInterview
        ? 'Interview updated successfully!'
        : 'Interview scheduled successfully!';
      if (calendarError) {
        const calendarMsg = calendarError?.isQuotaError
          ? 'Google Calendar daily quota has been reached, so the calendar invite was not sent. The interview itself has been saved.'
          : calendarError?.message?.includes('authentication expired')
            ? 'Google Calendar sign-in expired, so the calendar invite was not sent. The interview itself has been saved. Please sign in to Google Calendar again.'
            : 'The interview was saved, but the Google Calendar invite could not be created. You can try again or add it manually.';
        alert(`${baseMsg}\n\n${calendarMsg}`);
      } else {
        alert(baseMsg);
      }
      
      // Reset form and refresh history
      handleCancelEdit();
      if (formData.jobId) {
        const updatedHistory = getJobInterviews(formData.jobId).sort((a, b) => a.round - b.round);
        setSelectedJobHistory(updatedHistory);
      }

    } catch (error) {
      console.error('Error scheduling interview:', error);
      // Google Calendar errors are handled inline above — this catch only
      // fires for validation / database / unexpected errors.
      const msg =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to schedule interview. Please try again.';
      alert(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <ConfirmDialog
        isOpen={showCalendarWarning}
        onConfirm={handleConfirmWithoutCalendar}
        onCancel={handleCancelWarning}
        title="Google Calendar Not Connected"
        message={
          <p>
            You are about to schedule an interview without being connected to Google Calendar. 
            The interview will be saved locally but <strong>no calendar invite will be sent</strong> to the attendees.
          </p>
        }
        type="warning"
        confirmText="Confirm"
        cancelText="Cancel"
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onConfirm={confirmDeleteInterview}
        onCancel={cancelDeleteInterview}
        title="Delete Interview"
        message="Are you sure you want to delete this interview? This action cannot be undone."
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
        confirmDanger={true}
      />

      <div className="page-header">
        <div className="page-header-text">
          <h1>Schedule Interview</h1>
          <p>Book a new interview and sync it with your Google Calendar.</p>
        </div>
      </div>

      <div className="card card-compact">
        <div className="interview-scheduler-google-section">
          {isGoogleSignedIn ? (
            <>
              <p className="interview-scheduler-connected">
                Connected to Google Calendar · {calendarClientCount}/{getTotalClients()} clients
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                {calendarClientCount < getTotalClients() && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleGoogleSignIn}
                    title="Connect the remaining Google account(s) for quota failover"
                  >
                    Connect additional client ({getTotalClients() - calendarClientCount} left)
                  </button>
                )}
                <button className="btn btn-danger-outline btn-sm" onClick={handleGoogleSignOut}>
                  Sign out
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="interview-scheduler-not-connected">
                Not connected to Google Calendar
              </p>
              <button className="btn btn-primary btn-sm" onClick={handleGoogleSignIn}>
                Sign in to Google Calendar
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card">
        <h3>{editingInterview ? 'Edit Interview' : 'Schedule New Interview'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-2">
            <div className="form-group">
              <label>Select Job *</label>
              <select
                name="jobId"
                value={formData.jobId}
                onChange={handleChange}
                required
                disabled={!!preSelectedJobId}
              >
                <option value="">Choose a job...</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id} disabled={job.status === 'rejected'}>
                    {formatJobForDisplay(job)} {job.status === 'rejected' ? '(Rejected)' : ''}
                  </option>
                ))}
              </select>
              {preSelectedJobId && (
                <small className="interview-scheduler-preselected">
                  ✓ Job pre-selected from Jobs page
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Interview Round *</label>
              <select
                name="round"
                value={formData.round}
                onChange={handleChange}
                required
              >
                {getCurrentAvailableRounds().map(round => (
                  <option key={round} value={round}>Round {round}</option>
                ))}
              </select>
              {formData.jobId && (
                <small className="interview-scheduler-round-info">
                  {selectedJobHistory.length > 0 
                    ? (() => {
                        const hasRejected = selectedJobHistory.some(i => i.status === 'rejected');
                        if (hasRejected) {
                          return 'Cannot schedule more rounds - previous interview was rejected';
                        }
                        const availableRounds = getAvailableRoundsForJob(selectedJobHistory, true);
                        return `Completed rounds: ${[...new Set(selectedJobHistory.map(i => i.round))].sort((a,b) => a-b).join(', ')}. Next available: ${availableRounds.join(', ') || 'None'}`;
                      })()
                    : 'Start with Round 1'
                  }
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Interview Type</label>
              <select
                name="interviewType"
                value={formData.interviewType}
                onChange={handleChange}
              >
                {Object.entries(INTERVIEW_TYPES).map(([key, value]) => (
                  <option key={key} value={value}>{value}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    name="isFinalRound"
                    checked={formData.isFinalRound}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                </div>
                <div className="checkbox-content">
                  <span className="checkbox-title">Final Round</span>
                  <span className="checkbox-description">Will show in Offer Awaiting column after completion</span>
                </div>
              </label>
            </div>

            <div className="form-group">
              <label>Duration (minutes)</label>
              <select
                name="duration"
                value={formData.duration}
                onChange={handleChange}
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>1 hour</option>
                <option value={75}>1 hr 15 min</option>
                <option value={90}>1.5 hours</option>
                <option value={105}>1 hr 45 min</option>
                <option value={120}>2 hours</option>
                <option value={150}>2.5 hours</option>
                <option value={180}>3 hours</option>
              </select>
            </div>

            <div className="form-group">
              <label>Date & Time *</label>
              <input
                type="datetime-local"
                name="dateTime"
                value={formData.dateTime}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Attendees (Email addresses, comma-separated)</label>
              
              {/* Default attendees as clickable chips */}
              {defaultAttendees.length > 0 && (
                <div className="attendee-chips">
                  {defaultAttendees.map(email => {
                    const isSelected = formData.attendees.split(',').map(e => e.trim()).includes(email);
                    return (
                      <span
                        key={email}
                        onClick={() => toggleAttendeeInForm(email)}
                        className={`attendee-chip ${isSelected ? 'is-selected' : ''}`}
                      >
                        {email}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeDefaultAttendee(email); }}
                          className="attendee-chip-remove"
                          title="Remove from defaults"
                          aria-label={`Remove ${email}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Add new default attendee */}
              <div className="attendee-add-row">
                <input
                  type="email"
                  value={newDefaultEmail}
                  onChange={(e) => setNewDefaultEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDefaultAttendee())}
                  placeholder="Add default attendee email…"
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addDefaultAttendee}
                >
                  Add
                </button>
              </div>

              <input
                type="text"
                name="attendees"
                value={formData.attendees}
                onChange={handleChange}
                placeholder="Or type emails manually: john@company.com, jane@company.com"
              />
              <small className="attendee-help">
                Click chips above to toggle, or type additional emails below.
              </small>
            </div>

            <div className="form-group">
              <label>Recruiter Name</label>
              <input
                type="text"
                name="recruiterName"
                value={formData.recruiterName}
                onChange={handleChange}
                placeholder="Enter recruiter name"
              />
            </div>

            <div className="form-group">
              <label>Meeting Link</label>
              <input
                type="url"
                name="meetingLink"
                value={formData.meetingLink}
                onChange={handleChange}
                placeholder="https://zoom.us/j/..."
              />
            </div>


          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Additional notes for the interview..."
            />
          </div>

          <div className="interview-scheduler-actions">
            {editingInterview && (
              <button type="button" className="btn btn-secondary" onClick={handleCancelEdit} disabled={isSubmitting}>
                Cancel
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting
                ? 'Scheduling…'
                : (
                  <>
                    {editingInterview ? 'Update Interview' : 'Schedule Interview'}
                    {isGoogleSignedIn && (editingInterview ? ' & Update Calendar' : ' & Add to Calendar')}
                  </>
                )
              }
            </button>
          </div>
        </form>
      </div>

      {selectedJobHistory.length > 0 && (
        <div className="card">
          <h3>Previous Interview History</h3>
          <div>
            {selectedJobHistory.map(interview => {
              const interviewDate = new Date(interview.dateTime);
              return (
                <div key={interview.id} className="interview-scheduler-history-item">
                  <div className="grid grid-2">
                    <div>
                      <h5>Round {interview.round} - {interview.interviewType}</h5>
                      <p><strong>Date:</strong> {interviewDate.toLocaleDateString()} at {interviewDate.toLocaleTimeString()}</p>
                      <p><strong>Duration:</strong> {interview.duration} minutes</p>
                      {interview.recruiterName && <p><strong>Recruiter:</strong> {interview.recruiterName}</p>}
                      <p><strong>Status:</strong> 
                        <span className={`status-badge status-${interview.status || 'waiting'}`}>
                          {interview.status || 'waiting'}
                        </span>
                      </p>
                    </div>
                    <div>
                      {interview.feedback ? (
                        <div>
                          <h6 className="interview-scheduler-feedback-available">✓ Feedback Available</h6>
                          <p><strong>Taken by:</strong> {interview.takenBy}</p>
                          <p><strong>Feedback:</strong> {interview.feedback}</p>
                        </div>
                      ) : (
                        <p className="interview-scheduler-feedback-pending">⏳ Feedback Pending</p>
                      )}
                      <div className="interview-scheduler-history-actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEditInterview(interview)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger-outline btn-sm"
                          onClick={() => handleDeleteInterview(interview.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>

                  {(interview.resumeLink || interview.jdLink || interview.meetingLink) && (
                    <div className="interview-scheduler-links">
                      <strong>Links:</strong>
                      {interview.resumeLink && <a className="interview-scheduler-link" href={interview.resumeLink} target="_blank" rel="noopener noreferrer">Resume</a>}
                      {interview.jdLink && <a className="interview-scheduler-link" href={interview.jdLink} target="_blank" rel="noopener noreferrer">Job Description</a>}
                      {interview.meetingLink && <a className="interview-scheduler-link" href={interview.meetingLink} target="_blank" rel="noopener noreferrer">Meeting</a>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {jobs.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-title">No jobs available</div>
            <div className="empty-state-desc">Add a job first before scheduling interviews.</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewScheduler;